import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { albumData } from '@/mocks/album';
import { SITE_ORIGIN } from '@/config/site';

export default function HeroSection() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // 公開 URL から組み立てる。`window.location` を見ないのは、プリレンダー（Node）と
  // ブラウザで値が食い違うと共有リンクの href がずれるため（→ @/config/site）。
  const shareUrl = `${SITE_ORIGIN}${pathname}`;
  // 共有文言は表示中の言語で作る（英語で見ている人が日本語の投稿文を掴まされないように）
  const shareText = t('hero.shareText', { title: albumData.title, artist: albumData.artist });
  const jacketAlt = t('hero.jacketAlt', { title: albumData.title, artist: albumData.artist });

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    line: `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}`,
  };

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden bg-background-50">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
          backgroundImage: `url('/assets/hero-bg.webp')`,
        }}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/40 to-background-50"></div>
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 py-24 md:py-32">
        <div className="flex flex-col items-center text-center gap-8 md:gap-10">
          <p className="text-primary-400 text-sm md:text-base tracking-[0.3em] animate-fade-in-up">
            DIGITAL SINGLE
          </p>

          <div className="w-72 h-72 md:w-96 md:h-96 lg:w-[420px] lg:h-[420px] animate-fade-in-up animate-delay-200">
            <div className="w-full h-full rounded-lg overflow-hidden animate-pulse-glow">
              <img
                src="/assets/jacket.webp"
                alt={jacketAlt}
                title={jacketAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="animate-fade-in-up animate-delay-400">
            <h1 className="text-3xl md:text-5xl lg:text-7xl font-black text-foreground-950 leading-[1.1] mb-3 tracking-tight">
              Who is the machine?
            </h1>
            <p className="text-xl md:text-2xl text-foreground-800 mb-2">
              {albumData.artist}
            </p>
            <p className="text-sm text-foreground-600 tracking-wider">
              {albumData.releaseDate} RELEASE
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 animate-fade-in-up animate-delay-600">
            <a
              href={albumData.streamingLinks.spotify}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-400 transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-spotify-fill text-lg"></i>
              Spotify
            </a>
            <a
              href={albumData.streamingLinks.appleMusic}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-200/60 text-foreground-900 hover:bg-background-300/80 border border-background-300/40 transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-apple-fill text-lg"></i>
              Apple Music
            </a>
            <a
              href={albumData.streamingLinks.youtube}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-200/60 text-foreground-900 hover:bg-background-300/80 border border-background-300/40 transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-youtube-fill text-lg"></i>
              YouTube Music
            </a>
          </div>

          <div className="flex items-center gap-3 animate-fade-in-up animate-delay-800">
            <span className="text-xs text-foreground-600 tracking-wider whitespace-nowrap">SHARE</span>
            <a
              href={shareLinks.twitter}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-9 h-9 rounded-full bg-background-200/50 hover:bg-background-300/70 border border-background-300/30 flex items-center justify-center transition-all duration-300 cursor-pointer"
              aria-label={t('hero.shareOnX')}
            >
              <i className="ri-twitter-x-fill text-foreground-700 text-sm"></i>
            </a>
            <a
              href={shareLinks.facebook}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-9 h-9 rounded-full bg-background-200/50 hover:bg-background-300/70 border border-background-300/30 flex items-center justify-center transition-all duration-300 cursor-pointer"
              aria-label={t('hero.shareOnFacebook')}
            >
              <i className="ri-facebook-fill text-foreground-700 text-sm"></i>
            </a>
            <a
              href={shareLinks.line}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-9 h-9 rounded-full bg-background-200/50 hover:bg-background-300/70 border border-background-300/30 flex items-center justify-center transition-all duration-300 cursor-pointer"
              aria-label={t('hero.shareOnLine')}
            >
              <i className="ri-line-fill text-foreground-700 text-sm"></i>
            </a>
            <button
              onClick={() => { navigator.clipboard.writeText(shareUrl); }}
              className="w-9 h-9 rounded-full bg-background-200/50 hover:bg-background-300/70 border border-background-300/30 flex items-center justify-center transition-all duration-300 cursor-pointer"
              aria-label={t('hero.copyUrl')}
            >
              <i className="ri-link text-foreground-700 text-sm"></i>
            </button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-fade-in-up animate-delay-800">
          <div className="w-5 h-8 rounded-full border border-foreground-600/30 flex items-start justify-center pt-1.5">
            <div className="w-1 h-1.5 rounded-full bg-primary-500/60 animate-bounce"></div>
          </div>
        </div>
      </div>
    </section>
  );
}